/**
 * CommandIsland - Top floating island with search trigger and location display
 *
 * The most prominent UI element. Contains:
 * - Universal search trigger (Cmd+K / Ctrl+K)
 * - Location name (static for single-location FBOs)
 * - Placement mode instructions (when active)
 */

import { useState, useEffect } from 'react'
import { Search, MapPin, ChevronDown, Check, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Island } from './Island'
import { UniversalSearch } from './UniversalSearch'
import { useLocationAccess } from '@/hooks/useLocationAccess'
import { useAppContext, useLocationNavigation } from '@/hooks/routing'
import { getLocationColor, NETWORK_COLOR } from '@/lib/locationColors'
import { normalizeSlug } from '@/lib/slugs'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface CommandIslandProps {
  locationName: string
  isPlacementMode?: boolean
  minimized?: boolean
  onFocusAircraft: (allocationId: string) => void
  onHighlightAircraft: (allocationIds: string[]) => void
  onFocusArea: (areaId: string) => void
}

export function CommandIsland({
  locationName,
  isPlacementMode,
  minimized,
  onFocusAircraft,
  onHighlightAircraft,
  onFocusArea,
}: CommandIslandProps) {
  const [searchOpen, setSearchOpen] = useState(false)
  const [locOpen, setLocOpen] = useState(false)
  const { accessibleLocations, hasMultipleLocations } = useLocationAccess()
  const { scope, locationSlug } = useAppContext()
  const { switchLocation, goToOrgView } = useLocationNavigation()
  const isNetwork = scope === 'org'

  // Build display label: "Location Name (CODE)"
  const currentLoc = accessibleLocations.find(
    (l) => normalizeSlug(l.location_code) === locationSlug
  )
  const locationCode = currentLoc?.iata_code || currentLoc?.location_code?.slice(0, 3).toUpperCase()
  const displayLabel = locationCode
    ? `${locationName} (${locationCode})`
    : locationName

  // Cmd+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // During placement mode, show placement instructions instead
  if (isPlacementMode) {
    return (
      <Island
        minimized={false}
        className="absolute top-4 left-1/2 -translate-x-1/2 z-20 px-6 py-3"
      >
        <div className="flex items-center gap-4 text-sm">
          <span><kbd className="px-1.5 py-0.5 text-xs font-mono bg-muted rounded">Click</kbd> to place</span>
          <span className="text-muted-foreground">|</span>
          <span><kbd className="px-1.5 py-0.5 text-xs font-mono bg-muted rounded">Scroll</kbd> to rotate</span>
          <span className="text-muted-foreground">|</span>
          <span><kbd className="px-1.5 py-0.5 text-xs font-mono bg-muted rounded">ESC</kbd> to cancel</span>
        </div>
      </Island>
    )
  }

  return (
    <>
      <Island
        minimized={minimized}
        className="absolute top-4 left-1/2 -translate-x-1/2 z-20 px-2 py-1.5"
      >
        <div className="flex items-center gap-3">
          {/* Search trigger button */}
          <button
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg',
              'text-sm text-muted-foreground',
              'hover:bg-accent/50 transition-colors',
              'min-w-[200px]'
            )}
            onClick={() => setSearchOpen(true)}
          >
            <Search className="w-4 h-4" />
            <span>Search...</span>
            <kbd className="ml-auto px-1.5 py-0.5 text-[10px] font-mono bg-muted rounded">
              {navigator.platform.includes('Mac') ? '⌘K' : 'Ctrl+K'}
            </kbd>
          </button>

          {/* Separator */}
          <div className="w-px h-5 bg-border" />

          {/* Location dropdown / static name */}
          {hasMultipleLocations ? (
            <Popover open={locOpen} onOpenChange={setLocOpen}>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm',
                    'hover:bg-accent/50 transition-colors'
                  )}
                >
                  <span className="font-medium truncate max-w-[200px]">{displayLabel}</span>
                  <ChevronDown className={cn(
                    'w-3 h-3 text-muted-foreground shrink-0 transition-transform duration-200',
                    locOpen && 'rotate-180'
                  )} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="end"
                className="w-[240px] p-1.5"
                sideOffset={8}
              >
                {/* Location items */}
                <div className="max-h-[280px] space-y-0.5 overflow-y-auto">
                  {accessibleLocations.map((loc) => {
                    const slug = normalizeSlug(loc.location_code)
                    const isSelected = !isNetwork && locationSlug === slug
                    const color = getLocationColor(loc.location_code)
                    const code = loc.iata_code || loc.location_code?.slice(0, 3).toUpperCase() || '???'

                    return (
                      <button
                        key={loc.id}
                        onClick={() => { setLocOpen(false); switchLocation(slug) }}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent',
                          isSelected && 'bg-accent'
                        )}
                      >
                        <span
                          className="font-mono text-[10px] font-bold shrink-0 w-7 text-center"
                          style={{ color }}
                        >
                          {code}
                        </span>
                        <span className="flex-1 truncate">{loc.name}</span>
                        {isSelected && <Check className="h-3.5 w-3.5 shrink-0" style={{ color }} />}
                      </button>
                    )
                  })}
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <div className="flex items-center gap-1.5 px-2 text-sm">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-medium truncate max-w-[200px]">{displayLabel}</span>
            </div>
          )}

          {/* Separator */}
          <div className="w-px h-5 bg-border" />

          {/* Globe icon — All Locations (multi-location only, right of location) */}
          {hasMultipleLocations && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {alert("Coming Soon");return;goToOrgView({ resource: 'dashboard' })}}  
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-lg shrink-0 transition-colors',
                    isNetwork
                      ? 'hover:bg-accent/50'
                      : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                  )}
                >
                  <Globe
                    className="w-4 h-4"
                    style={isNetwork ? { color: NETWORK_COLOR } : undefined}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>
                All Locations
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </Island>

      {/* Search dialog (rendered in portal) */}
      <UniversalSearch
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onFocusAircraft={onFocusAircraft}
        onHighlightAircraft={onHighlightAircraft}
        onFocusArea={onFocusArea}
      />
    </>
  )
}
